/**
 * Background layers and the contrast scrim.
 *
 * The scrim is the load-bearing idea. Any palette can sit on any background
 * because a tuned overlay of the palette's own ground colour always sits
 * between the artwork and the text -- so text legibility cannot be broken by
 * any of the 144 combinations. It is what makes "no choices" produce good
 * pages instead of merely fewer bad ones.
 */
import type { BackgroundId, Palette } from './theme.js'
import { BACKGROUNDS } from './theme.js'

/** #RRGGBB -> `rgba(r,g,b,alpha)`. */
export function rgba(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex)
  return `rgba(${r},${g},${b},${alpha})`
}

export function hexToRgb(hex: string): readonly [number, number, number] {
  const clean = hex.replace('#', '')
  const full =
    clean.length === 3
      ? clean.split('').map((c) => c + c).join('')
      : clean
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ] as const
}

/**
 * Deterministic PRNG (mulberry32) seeded from the slug.
 *
 * The starfield must be stable: regenerating the page on every edit should not
 * reshuffle the stars, or a founder tweaking their tagline sees the artwork
 * jump for no reason.
 */
function seededRandom(seed: string): () => number {
  let h = 1779033703 ^ seed.length
  for (let i = 0; i < seed.length; i += 1) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353)
    h = (h << 13) | (h >>> 19)
  }
  let a = h >>> 0
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function starfield(palette: Palette, seed: string): string {
  const rand = seededRandom(seed)
  const stars: string[] = []
  for (let i = 0; i < 44; i += 1) {
    const x = (rand() * 100).toFixed(2)
    const y = (rand() * 100).toFixed(2)
    const size = (rand() * 1.6 + 0.5).toFixed(2)
    const alpha = (rand() * 0.5 + 0.25).toFixed(2)
    stars.push(
      `radial-gradient(${size}px ${size}px at ${x}% ${y}%, ${rgba(palette.text, Number(alpha))} 50%, transparent 51%)`,
    )
  }
  return stars.join(',')
}

/** The `background-image` stack for a given background choice. */
export function backgroundLayers(
  id: BackgroundId,
  palette: Palette,
  seed: string,
  assetOrigin: string,
): string {
  switch (id) {
    case 'solid':
      return 'none'
    case 'mesh':
      return [
        `radial-gradient(ellipse 80% 60% at 15% 0%, ${rgba(palette.accent, 0.28)}, transparent 60%)`,
        `radial-gradient(ellipse 70% 55% at 90% 15%, ${rgba(palette.surface, 0.9)}, transparent 62%)`,
        `radial-gradient(ellipse 90% 70% at 60% 100%, ${rgba(palette.surface, 0.7)}, transparent 65%)`,
      ].join(',')
    case 'blueprint':
      // Two grids, a fine one inside a coarse one -- the drafting-paper feel
      // of the reference without reproducing any of its artwork.
      return [
        `repeating-linear-gradient(0deg, ${palette.line} 0 1px, transparent 1px 80px)`,
        `repeating-linear-gradient(90deg, ${palette.line} 0 1px, transparent 1px 80px)`,
        `repeating-linear-gradient(0deg, ${rgba(palette.text, 0.05)} 0 1px, transparent 1px 16px)`,
        `repeating-linear-gradient(90deg, ${rgba(palette.text, 0.05)} 0 1px, transparent 1px 16px)`,
        `radial-gradient(ellipse 70% 60% at 50% 0%, ${rgba(palette.accent, 0.16)}, transparent 70%)`,
      ].join(',')
    case 'rings':
      // Concentric orbits, offset below the fold so the arcs sweep up behind
      // the text rather than ringing it like a target.
      return [
        `repeating-radial-gradient(circle at 50% 118%, transparent 0 58px, ${palette.line} 58px 59px)`,
        `radial-gradient(circle at 50% 118%, ${rgba(palette.accent, 0.22)}, transparent 55%)`,
        `radial-gradient(ellipse 90% 60% at 50% 0%, ${rgba(palette.surface, 0.65)}, transparent 70%)`,
      ].join(',')
    case 'dawn':
      // A horizon: ground below, atmosphere thinning upward.
      return [
        `linear-gradient(180deg, transparent 0 58%, ${palette.line} 58% calc(58% + 1px), transparent calc(58% + 1px))`,
        `radial-gradient(ellipse 120% 42% at 50% 60%, ${rgba(palette.accent, 0.38)}, transparent 70%)`,
        `linear-gradient(180deg, ${rgba(palette.surface, 0.9)} 0%, transparent 45%)`,
        `linear-gradient(0deg, ${rgba(palette.surface, 0.75)} 0%, transparent 38%)`,
      ].join(',')
    case 'starfield':
      return `${starfield(palette, seed)},radial-gradient(ellipse 90% 70% at 50% 110%, ${rgba(palette.accent, 0.22)}, transparent 60%)`
    case 'launch':
    case 'orbit':
      return duotone(id, palette, assetOrigin)
  }
}

/**
 * Duotone a grayscale photograph into the active palette.
 *
 * The source assets are baked to luminance at build time, then tinted here
 * with the classic two-layer recipe: a shadow colour multiplied underneath and
 * a highlight colour screened on top. Doing it in CSS rather than shipping six
 * pre-tinted copies of each photo means one cached asset serves every palette,
 * and the photographs read as part of the design system instead of as stock
 * imagery dropped behind it.
 *
 * Which colour plays which role depends on the palette: on a light palette the
 * accent is the darker of the two, so the roles swap.
 */
/**
 * Tint strengths, exported because contrast.test.ts models the worst-case
 * composite from them. Changing a number here moves a test, which is the point:
 * these are the difference between a photograph you can see and a flat wash.
 */
export const DUOTONE_SHADOW_ALPHA = 0.62
export const DUOTONE_HIGHLIGHT_ALPHA = 0.4

function duotone(id: BackgroundId, palette: Palette, assetOrigin: string): string {
  const shadow = palette.scheme === 'dark' ? palette.bg : palette.accent
  const highlight = palette.scheme === 'dark' ? palette.accent : palette.bg
  const photo = `image-set(url("${assetOrigin}/bg/${id}.avif") type("image/avif"), url("${assetOrigin}/bg/${id}.webp") type("image/webp"))`
  return [
    `linear-gradient(${rgba(highlight, DUOTONE_HIGHLIGHT_ALPHA)}, ${rgba(highlight, DUOTONE_HIGHLIGHT_ALPHA)})`,
    `linear-gradient(${rgba(shadow, DUOTONE_SHADOW_ALPHA)}, ${rgba(shadow, DUOTONE_SHADOW_ALPHA)})`,
    photo,
  ].join(',')
}

/**
 * Blend modes matching the layer stack from {@link backgroundLayers}.
 *
 * Only the photographic backgrounds blend; the generated ones are already
 * composed from palette colours and would only muddy.
 */
export function backgroundBlend(id: BackgroundId): string {
  return BACKGROUNDS[id].photographic ? 'screen,multiply,normal' : 'normal'
}

/**
 * The overlay that guarantees contrast.
 *
 * Photographs get a near-opaque wash because a launch plume is bright white
 * and would otherwise swallow light text; generated layers only need a nudge
 * to keep the accent glow from fighting the copy.
 */
/** Alpha directly behind the text on a photographic backdrop. */
export const PHOTO_SCRIM_CENTRE = 0.52

export function scrimLayers(id: BackgroundId, palette: Palette): string {
  const photographic = BACKGROUNDS[id].photographic
  if (!photographic) {
    return `linear-gradient(${rgba(palette.bg, 0.35)}, ${rgba(palette.bg, 0.55)})`
  }
  // Only the band behind the text needs protecting. A flat wash across the
  // whole frame is what turned these into plain colour before -- the edges can
  // stay clear, because nothing is read there.
  return [
    `radial-gradient(ellipse 58% 42% at 50% 46%, ${rgba(palette.bg, PHOTO_SCRIM_CENTRE)} 0%, ${rgba(palette.bg, PHOTO_SCRIM_CENTRE * 0.7)} 55%, transparent 78%)`,
    `linear-gradient(180deg, ${rgba(palette.bg, 0.5)} 0%, transparent 28%, transparent 68%, ${rgba(palette.bg, 0.62)} 100%)`,
  ].join(',')
}
